import { supabase } from '@/lib/supabase'

export type TimeSavingMode = 'live' | 'backfill'
export type DataClassification = 'real' | 'demo' | 'test'

export interface AdminTimeSavingRow {
  organization_id: string
  organization_name: string
  data_classification: DataClassification
  user_id: string | null
  action_key: string
  module: string
  local_day: string
  organization_timezone: string
  mode: TimeSavingMode
  event_count: number
  raw_seconds_saved: number
  chain_capped_seconds: number
  workforce_capped_seconds: number
  active_user_count: number
  refreshed_at: string
}

export interface AdminTimeSavingParams {
  from: string
  to: string
  includeDemo: boolean
}

export type BetaEnrollmentStatus = 'active' | 'revoked'

export interface BetaProgramOrganization {
  organization_id: string
  organization_name: string
  organization_slug: string
  organization_status: string
  data_classification: DataClassification
  status: BetaEnrollmentStatus
  notes: string | null
  enrolled_at: string
  enrolled_by_email: string | null
  revoked_at: string | null
  revoked_by_email: string | null
}

export interface BetaProgramSnapshot {
  program_key: string
  display_name: string
  enabled: boolean
  organizations: BetaProgramOrganization[]
}

export const timeSavedApi = {
  adminReport: async ({ from, to, includeDemo }: AdminTimeSavingParams): Promise<AdminTimeSavingRow[]> => {
    const { data, error } = await supabase.rpc('admin_time_saving_report', {
      p_from: from,
      p_to: to,
      p_include_demo: includeDemo,
    })

    if (error) throw error
    if (!Array.isArray(data)) throw new Error('Unexpected time-saved report response')
    return data as unknown as AdminTimeSavingRow[]
  },

  betaProgram: async (programKey = 'time_saved'): Promise<BetaProgramSnapshot> => {
    const { data, error } = await supabase.rpc('admin_beta_program_snapshot', {
      p_program_key: programKey,
    })
    if (error) throw error
    if (!data || typeof data !== 'object') throw new Error('Unexpected beta program response')
    return data as unknown as BetaProgramSnapshot
  },

  setBetaEnabled: async (enabled: boolean, programKey = 'time_saved'): Promise<void> => {
    const { error } = await supabase.rpc('admin_set_beta_program_enabled', {
      p_program_key: programKey,
      p_enabled: enabled,
    })
    if (error) throw error
  },

  enrollBetaOrganization: async (organizationId: string, notes: string | null, programKey = 'time_saved'): Promise<void> => {
    const { error } = await supabase.rpc('admin_enroll_beta_organization', {
      p_program_key: programKey,
      p_organization_id: organizationId,
      p_notes: notes,
    })
    if (error) throw error
  },

  revokeBetaOrganization: async (organizationId: string, programKey = 'time_saved'): Promise<void> => {
    const { error } = await supabase.rpc('admin_revoke_beta_organization', {
      p_program_key: programKey,
      p_organization_id: organizationId,
    })
    if (error) throw error
  },
}
